import { create } from 'zustand';
import { ocdProfileService } from './ocdProfileService';
import { LearningQuestion, OcdInfo, OcdProfile } from './ocdProfileTypes';

interface OcdProfileState {
  ocdProfile: OcdProfile | null;
  loadOcdProfile: (userId: string) => Promise<void>;
  saveOcdInfoFromAi: (userId: string, key: string, info: string[], mode: 'append' | 'replace') => Promise<void>;
  saveOcdInfoFromUser: (userId: string, key: string, info: string[]) => Promise<void>;
  saveLearningQuestion: (userId: string, questionId: string, question: string, answers: string[]) => Promise<void>;
}

const initialState = {
  ocdProfile: null,
};

const useOcdProfileStore = create<OcdProfileState>()((set, get) => ({
  ...initialState,

  loadOcdProfile: async (userId: string) => {
    const currentState = get();
    if (currentState.ocdProfile) {
      return;
    }

    const ocdProfile = await ocdProfileService.getOcdProfile(userId);
    set({ ocdProfile: ocdProfile });
  },

  saveOcdInfoFromAi: async (userId: string, key: string, info: string[], mode: 'append' | 'replace') => {
    let currentState = get();

    if (!currentState.ocdProfile) {
      await currentState.loadOcdProfile(userId);
      currentState = get();
    }

    let infoToSave = currentState.ocdProfile?.ocdInfos[key];
    if (!infoToSave) {
      infoToSave = {
        id: key,
        userProvided: [],
        inferred: info,
      };
    } else {
      if (mode === 'replace') {
        infoToSave.inferred = info;
      } else {
        infoToSave.inferred = [...infoToSave.inferred, ...info];
      }
    }
    await ocdProfileService.saveOcdInfo(userId, infoToSave, key);

    if (currentState.ocdProfile) {
      set({ ocdProfile: { ...currentState.ocdProfile, ocdInfos: { ...currentState.ocdProfile.ocdInfos, [key]: infoToSave } } });
    } else {
      await currentState.loadOcdProfile(userId);
    }
  },

  saveOcdInfoFromUser: async (userId: string, key: string, info: string[]) => {
    let currentState = get();

    if (!currentState.ocdProfile) {
      await currentState.loadOcdProfile(userId);
      currentState = get();
    }

    const newOcdInfo: OcdInfo = {
      id: key,
      userProvided: info,
      inferred: [],
    };
    await ocdProfileService.saveOcdInfo(userId, newOcdInfo, key);

    if (currentState.ocdProfile) {
      set({ ocdProfile: { ...currentState.ocdProfile, ocdInfos: { ...currentState.ocdProfile.ocdInfos, [key]: newOcdInfo } } });
    }
  },

  saveLearningQuestion: async (userId: string, questionId: string, question: string, answers: string[]) => {
    const currentState = get();

    let learningQuestion = findLearningQuestion(currentState.ocdProfile?.learningQuestions, questionId);
    if (!learningQuestion) {
      learningQuestion = {
        id: questionId,
        question,
        answers,
      };
    } else {
      learningQuestion.question = question;
      learningQuestion.answers = answers;
    }

    const savedLearningQuestion = await ocdProfileService.saveLearningQuestion(userId, learningQuestion);

    if (currentState.ocdProfile) {
      set({
        ocdProfile: {
          ...currentState.ocdProfile,
          learningQuestions: [
            ...currentState.ocdProfile.learningQuestions.filter((lq) => lq.id !== learningQuestion.id),
            savedLearningQuestion,
          ] as LearningQuestion[],
        },
      });
    } else {
      await currentState.loadOcdProfile(userId);
    }
  },
}));

function findLearningQuestion(learningQuestions: LearningQuestion[] | undefined, questionId: string): LearningQuestion | undefined {
  if (!learningQuestions) return undefined;
  return learningQuestions.find((lq) => lq.id === questionId);
}

export default useOcdProfileStore;
